import { useEffect, useMemo, useState } from "react"
import { Box, IconButton, TextField, Tooltip } from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import LinkOffIcon from "@mui/icons-material/LinkOff"
import { ColumnDef } from "@tanstack/react-table"
import { toast } from "react-toastify"

import { DataGridMui } from "src/shared/container/data-grid-mui"
import {
  IntegrationLocationsDocument,
  useIntegrationLocationsQuery,
  useRemoveIntegrationLocationMutation,
  useSetIntegrationLocationMutation
} from "src/graphql/quark/quark.type"

type LocationRow = {
  locationId: string
  name: string
  externalVendorId?: string | null
}

/**
 * Owns its own keystroke state so typing never touches LocationsTab — that
 * previously rebuilt the whole columns array (and the table under it) on
 * every character.
 */
const VendorIdCell = ({
  row,
  canEdit,
  saving,
  onSave,
  onRemove
}: {
  row: LocationRow
  canEdit: boolean
  saving: boolean
  onSave: (row: LocationRow, value: string) => Promise<void>
  onRemove: (row: LocationRow) => Promise<void>
}) => {
  const [value, setValue] = useState(row.externalVendorId ?? "")
  const dirty = value.trim() !== (row.externalVendorId ?? "")

  // Resyncs after a remote change (save confirmation, unlink) without
  // reacting to local typing, since that only ever updates `value` directly.
  useEffect(() => {
    setValue(row.externalVendorId ?? "")
  }, [row.externalVendorId])

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <TextField
        size="small"
        fullWidth
        disabled={!canEdit}
        placeholder="Not published"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty) void onSave(row, value)
        }}
      />
      <Tooltip title="Save">
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!canEdit || saving || !dirty}
            onClick={() => void onSave(row, value)}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Stop publishing this store">
        <span>
          <IconButton
            size="small"
            color="error"
            disabled={!canEdit || !row.externalVendorId}
            onClick={() => void onRemove(row)}
          >
            <LinkOffIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

const LocationsTab = ({
  connectionId,
  canEdit
}: {
  connectionId: string
  canEdit: boolean
}) => {
  const { data, loading } = useIntegrationLocationsQuery({
    variables: { connectionId },
    fetchPolicy: "network-only"
  })

  const refetchQueries = [
    { query: IntegrationLocationsDocument, variables: { connectionId } }
  ]

  const [setLocation, { loading: saving }] = useSetIntegrationLocationMutation({
    refetchQueries
  })
  const [removeLocation] = useRemoveIntegrationLocationMutation({
    refetchQueries
  })

  const rows: LocationRow[] = data?.fusionIntegrationLocations ?? []

  const handleSave = async (row: LocationRow, value: string) => {
    const externalVendorId = value.trim()
    if (!externalVendorId) {
      toast.error("Enter the vendor ID, or unlink to stop publishing this store.")
      return
    }

    try {
      await setLocation({
        variables: {
          input: { connectionId, locationId: row.locationId, externalVendorId }
        }
      })
      toast.success(`${row.name} linked to ${externalVendorId}`)
    } catch (error: any) {
      toast.error(error?.message ?? "Could not save the vendor ID")
    }
  }

  const handleRemove = async (row: LocationRow) => {
    try {
      await removeLocation({
        variables: { input: { connectionId, oneObjectId: row.locationId } }
      })
      toast.success(`${row.name} is no longer published`)
    } catch (error: any) {
      toast.error(error?.message ?? "Could not unlink the store")
    }
  }

  const columns: ColumnDef<LocationRow, any>[] = useMemo(
    () => [
      { header: "Store", accessorKey: "name" },
      {
        header: "Vendor ID on this integration",
        accessorKey: "externalVendorId",
        cell: ({ row }) => (
          <VendorIdCell
            row={row.original}
            canEdit={canEdit}
            saving={saving}
            onSave={handleSave}
            onRemove={handleRemove}
          />
        )
      }
    ],
    [canEdit, saving]
  )

  return (
    <DataGridMui
      id="integration-locations"
      data={rows}
      columns={columns}
      isLoading={loading}
      paginationMode="client"
      // initialState is captured once at mount, before rows load — a fixed
      // ceiling avoids seeding pageSize from an empty first-render array.
      initialPageSize={1000}
      // LocationRow has no `id`; without this every row falls back to the
      // same undefined key, so React remounts the cell being edited on every keystroke.
      getRowId={(row: LocationRow) => row.locationId}
      hidePagination
      noDataComponent={<Box p={3}>No stores found.</Box>}
    />
  )
}

export default LocationsTab
